import AxiosService from '@/api/ApiBase';
import {
  IpiLoadGuideCreateNotePayload,
  IpiLoadGuideDeleteNotePayload,
  IpiLoadGuideUpdateNotePayload,
  IpiLoadGuideUpdatePayload,
} from '@/types/ipi/load-guide/ipi.load-guide.types';
import {
  ExportLoadGuide,
  ExportLoadGuideCreatePayload,
  ExportLoadGuideFinalize,
} from '@/types/export/load/guide/load-guide.entity';
import { FilteredItemsResponse, FilterOptionsParams } from '@/types/service/filters.service';
import { ExportLoadGuideAmendPayload } from '@/types/export/load/guide/load-guide.amend';
import { OnHandEntity } from '@/types/on-hand/on-hand.entity';
import { ExportLoadGuideFilters } from '@/types/export/load/guide/load-guide.filters';
import { LoadPlanEntity } from '@/types/export/load/plan/load-plan.entity';
import {
  ExportLoadGuideFinalizePayload,
  ExportLoadGuideFinalizePreviewOnHandsInvoiceResponse,
  ExportLoadGuideFinalizeResponse,
} from '@/types/export/load/guide/load-guide.finalize';
import { VirtualSearchPayload, VirtualSearchResponse } from '@/types/virtual-search';

const LOAD_GUIDE_BASE_URL = 'warehouse/export/loadguide';

// const LOAD_GUIDE_BASE_URL_1 = 'http://localhost:3000/api/export/loadguide';

class ExportLoadGuideService {
  create(data: ExportLoadGuideCreatePayload) {
    return AxiosService.post<ExportLoadGuide>(`${LOAD_GUIDE_BASE_URL}/create`, data);
  }

  update(data: IpiLoadGuideUpdatePayload) {
    return AxiosService.put<ExportLoadGuide>(`${LOAD_GUIDE_BASE_URL}`, data);
  }

  delete(id: string) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/delete/${id}`);
  }

  getOneById(id: string) {
    return AxiosService.get<ExportLoadGuide>(`${LOAD_GUIDE_BASE_URL}/get`, {
      params: {
        loadGuideId: id,
      },
    });
  }

  getAll(filters: ExportLoadGuideFilters) {
    return AxiosService.get<FilteredItemsResponse<ExportLoadGuide>>(
      `${LOAD_GUIDE_BASE_URL}/getAll`,
      {
        params: filters,
      }
    );
  }

  getFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`${LOAD_GUIDE_BASE_URL}/filter/options`, {
      params: { ...params, limit: 100 },
    });
  }

  virtualSearch(payload: VirtualSearchPayload<ExportLoadGuide>) {
    return AxiosService.post<VirtualSearchResponse<ExportLoadGuide>>(
      'warehouse/v2/export/loadGuide/search',
      payload
    );
  }

  finalizeDetails(payload: ExportLoadGuideFinalize) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/update/finalizeDetails`, payload);
  }

  markLoaded(onHandIds: string[], isLoaded: boolean) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/markLoaded`, { onHandIds, isLoaded });
  }

  updateNotesForLoadGuide(payload: IpiLoadGuideUpdateNotePayload) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/notes`, payload);
  }

  createNoteForLoadGuide(payload: IpiLoadGuideCreateNotePayload) {
    return AxiosService.post(`${LOAD_GUIDE_BASE_URL}/notes`, payload);
  }

  deleteNoteFromLoadGuide(payload: IpiLoadGuideDeleteNotePayload) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/notes`, {
      params: payload,
    });
  }

  amend(loadGuideId: string, payload: Omit<ExportLoadGuideAmendPayload, 'loadGuideId'>) {
    return AxiosService.put(`${LOAD_GUIDE_BASE_URL}/amend/${loadGuideId}`, payload);
  }

  availableInventoryItems(loadGuideId: string) {
    return AxiosService.get<OnHandEntity[]>(
      `${LOAD_GUIDE_BASE_URL}/inventory/available/${loadGuideId}`
    );
  }

  getContainerDetails(loadGuideId: string) {
    return AxiosService.get<LoadPlanEntity>(
      `${LOAD_GUIDE_BASE_URL}/containerDetails/${loadGuideId}`
    );
  }

  finalize(loadGuideId: ExportLoadGuideFinalizePayload) {
    return AxiosService.put<ExportLoadGuideFinalizeResponse>(
      `${LOAD_GUIDE_BASE_URL}/finalize/${loadGuideId}`
    );
  }

  getLoadGuideFinalizeOnHandsInvoicesPreview(loadGuideId: string) {
    return AxiosService.get<ExportLoadGuideFinalizePreviewOnHandsInvoiceResponse[]>(
      `${LOAD_GUIDE_BASE_URL}/${loadGuideId}/finalize/on-hands/preview`
    );
  }

  removeHandoffs(id: string) {
    return AxiosService.delete(`${LOAD_GUIDE_BASE_URL}/handOff/${id}`);
  }
}

export default new ExportLoadGuideService();
